// src/services/api.js — Production API layer
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
});

// Track if we're refreshing to prevent concurrent refresh loops
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (cb) => {
  refreshSubscribers.push(cb);
};

// ── Request interceptor: attach JWT token ──────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('solvit_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: handle 401 with token refresh ───────────────
api.interceptors.response.use(
  (response) => {
    // For blob responses, return the full response object
    if (response.config.responseType === 'blob') return response;
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status === 401 && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshRes = await api.post('/auth/refresh');
        const newToken = refreshRes?.data?.token || refreshRes?.token;

        if (newToken) {
          localStorage.setItem('solvit_token', newToken);
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          onRefreshed(newToken);
          isRefreshing = false;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed — clear session and redirect to login
        isRefreshing = false;
        refreshSubscribers = [];
        localStorage.removeItem('solvit_token');
        localStorage.removeItem('solvit_user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.errors?.[Object.keys(error?.response?.data?.errors || {})[0]]?.[0] ||
      error.message ||
      'Something went wrong';

    return Promise.reject(new Error(msg));
  }
);

// ── AUTH ───────────────────────────────────────────────────────────────
export const authService = {
  login:  (data) => api.post('/auth/login', data),
  logout: ()     => api.post('/auth/logout'),
  me:     ()     => api.get('/auth/me'),
  refresh:()     => api.post('/auth/refresh'),
};

// ── SUPER ADMIN DASHBOARD ──────────────────────────────────────────────
export const dashboardService = {
  getSuperAdminStats:  ()     => api.get('/admin/dashboard/stats'),
  getRevenueChart:     (p)    => api.get('/admin/dashboard/revenue', { params: { period: p } }),
  getActivityFeed:     (l=10) => api.get('/admin/dashboard/activity', { params: { limit: l } }),
  getTopSchools:       ()     => api.get('/admin/dashboard/top-schools'),
  getSubjectBreakdown: ()     => api.get('/admin/dashboard/subjects'),
  getSchoolDashboard:  ()     => api.get('/school/dashboard'),
  getTeacherDashboard: ()     => api.get('/teacher/dashboard'),
};

// ── STUDENTS ───────────────────────────────────────────────────────────
export const studentService = {
  getAll:            (p={}) => api.get('/admin/students', { params: p }),
  getById:           (id)   => api.get(`/admin/students/${id}`),
  create:            (d)    => api.post('/admin/students', d),
  update:            (id,d) => api.put(`/admin/students/${id}`, d),
  delete:            (id)   => api.delete(`/admin/students/${id}`),
  ban:               (id)   => api.post(`/admin/students/${id}/ban`),
  unban:             (id)   => api.post(`/admin/students/${id}/unban`),
  getSchoolStudents: (p={}) => api.get('/school/students', { params: p }),
  enrollStudent:     (d)    => api.post('/school/students/enroll', d),
  updateStudent:     (id,d) => api.put(`/school/students/${id}`, d),
  getMyStudents:     (p={}) => api.get('/teacher/students', { params: p }),
};

// ── SCHOOLS ────────────────────────────────────────────────────────────
export const schoolService = {
  getAll:           (p={}) => api.get('/admin/schools', { params: p }),
  getById:          (id)   => api.get(`/admin/schools/${id}`),
  create:           (d)    => api.post('/admin/schools', d),
  update:           (id,d) => api.put(`/admin/schools/${id}`, d),
  delete:           (id)   => api.delete(`/admin/schools/${id}`),
  renewLicense:     (id,d) => api.post(`/admin/schools/${id}/license`, d),
  getLicense:       ()     => api.get('/school/license'),
  getSchoolSettings:()     => api.get('/school/settings'),
  updateSettings:   (d)    => api.put('/school/settings', d),
};

// ── DOUBTS ────────────────────────────────────────────────────────────
export const doubtService = {
  getAll:          (p={}) => api.get('/admin/doubts', { params: p }),
  getById:         (id)   => api.get(`/admin/doubts/${id}`),
  review:          (id,d) => api.put(`/admin/doubts/${id}/review`, d),
  resolve:         (id,d) => api.post(`/admin/doubts/${id}/resolve`, d),
  assignToExpert:  (id,d) => api.post(`/admin/doubts/${id}/assign`, d),
  getStats:        ()     => api.get('/admin/doubts/stats'),
  getTeacherStats: ()     => api.get('/teacher/doubts/stats'),
  getMyDoubts:     (p={}) => api.get('/teacher/doubts', { params: p }),
  addAnswer:       (id,a) => api.post(`/teacher/doubts/${id}/answer`, { answer: a }),
  markOk:          (id)   => api.put(`/teacher/doubts/${id}/mark-ok`),
  getSchoolDoubts: (p={}) => api.get('/school/doubts/analytics', { params: p }),
};

// ── SUBSCRIPTIONS ─────────────────────────────────────────────────────
export const subscriptionService = {
  getAll:        (p={}) => api.get('/admin/subscriptions', { params: p }),
  getStats:      ()     => api.get('/admin/subscriptions/stats'),
  getMrrChart:   (p)    => api.get('/admin/subscriptions/mrr', { params: { period: p } }),
  getChurnChart: ()     => api.get('/admin/subscriptions/churn'),
  refund:        (id,d) => api.post(`/admin/subscriptions/${id}/refund`, d),
  getInvoices:   ()     => api.get('/subscriptions/invoices'),
  getInvoice:    (id)   => api.get(`/subscriptions/invoices/${id}`),
};

// ── PLANS ─────────────────────────────────────────────────────────────
export const planService = {
  getAll:  ()     => api.get('/admin/plans'),
  create:  (d)    => api.post('/admin/plans', d),
  update:  (id,d) => api.put(`/admin/plans/${id}`, d),
  delete:  (id)   => api.delete(`/admin/plans/${id}`),
};

// ── CONTENT ───────────────────────────────────────────────────────────
export const contentService = {
  getCards:   (p={}) => api.get('/admin/content/cards', { params: p }),
  createCard: (d)    => api.post('/admin/content/cards', d),
  updateCard: (id,d) => api.put(`/admin/content/cards/${id}`, d),
  toggleCard: (id)   => api.put(`/admin/content/cards/${id}/toggle`),
  getTests:   (p={}) => api.get('/admin/content/tests', { params: p }),
  createTest: (d)    => api.post('/admin/content/tests', d),
  updateTest: (id,d) => api.put(`/admin/content/tests/${id}`, d),
  getStats:   ()     => api.get('/admin/content/stats'),
  generate:   (d)    => api.post('/admin/content/generate', d),
};

// ── QUESTIONS ────────────────────────────────────────────────────────
export const questionService = {
  getAll:       (p={}) => api.get('/admin/questions', { params: p }),
  create:       (d)    => api.post('/admin/questions', d),
  update:       (id,d) => api.patch(`/admin/questions/${id}`, d),
  delete:       (id)   => api.delete(`/admin/questions/${id}`),
  bulkImport:   (d)    => api.post('/admin/questions/bulk-import', d),
};

// ── LIVE CLASSES ──────────────────────────────────────────────────────
export const liveClassService = {
  getAll:          (p={}) => api.get('/school/live-sessions', { params: p }),
  create:          (d)    => api.post('/school/live-sessions', d),
  update:          (id,d) => api.put(`/school/live-sessions/${id}`, d),
  delete:          (id)   => api.delete(`/school/live-sessions/${id}`),
  start:           (id)   => api.post(`/school/live-sessions/${id}/start`),
  end:             (id)   => api.post(`/school/live-sessions/${id}/end`),
  getRecordings:   (p={}) => api.get('/school/live-sessions/recordings', { params: p }),
  getStats:        ()     => api.get('/school/live-sessions/stats'),
  getMyClasses:    (p={}) => api.get('/teacher/live-sessions', { params: p }),
  getMyRecordings: (p={}) => api.get('/teacher/live-sessions/recordings', { params: p }),
  startMyClass:    (id)   => api.post(`/teacher/live-sessions/${id}/start`),
  endMyClass:      (id)   => api.post(`/teacher/live-sessions/${id}/end`),
};

// ── ASSIGNMENTS ───────────────────────────────────────────────────────
export const assignmentService = {
  getAll:           (p={}) => api.get('/school/assignments', { params: p }),
  getStats:         ()     => api.get('/school/assignments/stats'),
  getMyAssignments: (p={}) => api.get('/teacher/assignments', { params: p }),
  getById:          (id)   => api.get(`/teacher/assignments/${id}`),
  create:           (d)    => api.post('/teacher/assignments', d),
  update:           (id,d) => api.put(`/teacher/assignments/${id}`, d),
  delete:           (id)   => api.delete(`/teacher/assignments/${id}`),
  getSubmissions:   (id)   => api.get(`/teacher/assignments/${id}/submissions`),
  gradeSubmission:  (sId,d)=> api.put(`/teacher/assignments/submissions/${sId}/grade`, d),
  getMyStats:       ()     => api.get('/teacher/assignments/stats'),
};

// ── TEACHERS ──────────────────────────────────────────────────────────
export const teacherService = {
  getAll:         (p={}) => api.get('/school/teachers', { params: p }),
  create:         (d)    => api.post('/school/teachers', d),
  update:         (id,d) => api.put(`/school/teachers/${id}`, d),
  delete:         (id)   => api.delete(`/school/teachers/${id}`),
  getMySchedule:  ()     => api.get('/teacher/schedule'),
  createSchedule: (d)    => api.post('/teacher/schedule', d),
  updateSchedule: (id,d) => api.put(`/teacher/schedule/${id}`, d),
  deleteSchedule: (id)   => api.delete(`/teacher/schedule/${id}`),
};

// ── ANSWER CHECKER ────────────────────────────────────────────────────
export const answerCheckerService = {
  check:     (fd)   => api.post('/teacher/answer-check', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getRecent: (p={}) => api.get('/teacher/answer-check/history', { params: p }),
};

// ── PERFORMANCE ───────────────────────────────────────────────────────
export const performanceService = {
  getSchoolPerformance:  () => api.get('/school/performance'),
  getClassBreakdown:     () => api.get('/school/performance/classes'),
  getTeacherPerformance: () => api.get('/teacher/performance'),
  getMyClassBreakdown:   () => api.get('/teacher/performance/classes'),
};

// ── REPORTS ───────────────────────────────────────────────────────────
export const reportService = {
  downloadRevenue:      () => api.get('/admin/reports/revenue',       { responseType: 'blob' }),
  downloadSchools:      () => api.get('/admin/reports/schools',       { responseType: 'blob' }),
  downloadStudents:     () => api.get('/admin/reports/students',      { responseType: 'blob' }),
  downloadDoubts:       () => api.get('/admin/reports/doubts',        { responseType: 'blob' }),
  downloadSubs:         () => api.get('/admin/reports/subscriptions', { responseType: 'blob' }),
  downloadSchoolReport: () => api.get('/school/reports/monthly',      { responseType: 'blob' }),
  downloadStudentPdfs:  () => api.get('/school/reports/students',     { responseType: 'blob' }),
  sendParentBriefs:     () => api.post('/school/parent-briefs/send-all'),
  downloadMyReport:     () => api.get('/teacher/reports/monthly',     { responseType: 'blob' }),
  sendMyParentBriefs:   () => api.post('/teacher/parent-briefs/send'),
  generateCertificates:  () => api.post('/teacher/reports/certificates'),
  downloadWhatsApp:      () => api.get('/admin/reports/whatsapp',      { responseType: 'blob' }),
};

// ── SETTINGS ──────────────────────────────────────────────────────────
export const settingsService = {
  getGeneral:           ()  => api.get('/admin/settings'),
  updateGeneral:        (d) => api.put('/admin/settings', d),
  updateApiKeys:        (d) => api.put('/admin/settings/api-keys', d),
  testAiConnection:     ()  => api.post('/admin/settings/ai'),
  getSchoolSettings:    ()  => api.get('/school/settings'),
  updateSchoolSettings: (d) => api.put('/school/settings', d),
};

// ── NOTIFICATIONS ─────────────────────────────────────────────────────
export const notificationService = {
  getAll:      (p={}) => api.get('/notifications', { params: p }),
  getCount:    ()     => api.get('/notifications/unread-count'),
  markRead:    (id)   => api.put(`/notifications/${id}/read`),
  markAllRead: ()     => api.put('/notifications/read-all'),
};

// ── UTILITY: Download blob as file ────────────────────────────────────
export const downloadFile = (response, filename) => {
  // Handle both raw blob and axios response object
  const blob = response instanceof Blob ? response : new Blob([response.data || response]);
  const url  = window.URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export default api;

// ── ADMIN NOTIFICATIONS ──────────────────────────────────────────────
export const adminService = {
  notifyAll:    (d) => api.post('/admin/notify/all',    d),
  notifyClass:  (d) => api.post('/admin/notify/class',  d),
  notifySchool: (d) => api.post('/admin/notify/school', d),
};
